import { useCallback, useEffect, useRef, useState } from 'react';

import { writePropertyValue } from './targetResolvers.ts';
import type {
	EditPatch,
	EditableDescriptor,
	EditorSelection,
} from './types.ts';

const HOVER_OUTLINE = '2px solid rgba(59,130,246,0.5)';
const SELECTED_OUTLINE = '2px solid rgb(59,130,246)';
const DEBOUNCE_MS = 300;

/**
 * True while an element span meets the frame span on one axis. A span of zero
 * extent still marks a spot the customer can press, because a container whose
 * children all float or sit absolute measures flat while those children paint.
 * So a flat span compares as a point. An overlap test would reject every flat
 * span, at every scroll position, and the panel would never appear.
 */
const meetsFrameAxis = (
	start: number,
	end: number,
	frameExtent: number,
): boolean =>
	end <= start
		? start >= 0 && start <= frameExtent
		: end > 0 && start < frameExtent;

const sameRect = (a: DOMRect, b: DOMRect): boolean =>
	a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;

/**
 * Cross-window safe: avoid instanceof checks against parent window
 * constructors. Event targets from the iframe use the iframe's prototype chain,
 * so `target instanceof HTMLElement` (parent's constructor) always returns
 * false. Duck-type instead: check for `closest` (Element) or `parentElement`
 * (Text node).
 */
const elementFromTarget = (target: EventTarget | null): Element | null => {
	if (!target) return null;
	if ('closest' in target) return target as Element;
	if ('parentElement' in target) return (target as Node).parentElement;
	return null;
};

type UseIframeInteractionOptions = {
	iframeRef: React.RefObject<HTMLIFrameElement | null>;
	descriptors: EditableDescriptor[];
	onSerialize: () => void;
};

export function useIframeInteraction({
	iframeRef,
	descriptors,
	onSerialize,
}: UseIframeInteractionOptions) {
	const [selection, setSelection] = useState<EditorSelection | null>(null);
	const hoveredElementRef = useRef<HTMLElement | null>(null);
	const selectedElementRef = useRef<HTMLElement | null>(null);
	const serializeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

	// Where inside the selected element the customer clicked. Stored as an offset
	// from the element's top-left corner rather than as a viewport coordinate, so
	// the point still describes the same spot in the email after a scroll.
	const clickOffsetRef = useRef<{ x: number; y: number } | null>(null);

	const descriptorMap = useRef(new Map<string, EditableDescriptor>());
	useEffect(() => {
		descriptorMap.current.clear();
		for (const d of descriptors) {
			descriptorMap.current.set(d.id, d);
		}
	}, [descriptors]);

	const getIframeDoc = useCallback(() => {
		return iframeRef.current?.contentDocument ?? null;
	}, [iframeRef]);

	const computeAnchor = useCallback(
		(
			element: HTMLElement,
			offset: { x: number; y: number } | null,
		): {
			anchorRect: DOMRect;
			isAnchorVisible: boolean;
			frameRect: DOMRect;
		} | null => {
			const iframe = iframeRef.current;
			if (!iframe) return null;

			const elementRect = element.getBoundingClientRect();
			const iframeRect = iframe.getBoundingClientRect();

			// getBoundingClientRect inside the email returns coordinates in the
			// frame's own viewport, which spans 0 to iframeRect.height. So the
			// untranslated values below decide what the customer can still see, and
			// the translated ones place the panel on the page.

			// An element we cannot measure is no evidence that the customer cannot
			// see it. A detached node reports an all-zero box, and hiding the panel
			// on that would take its close button away and strand it open.
			const isMeasurable = elementRect.width > 0 || elementRect.height > 0;

			// The customer sees the element only where three boxes overlap: the
			// element, the frame's own viewport, and the window. The first two share
			// the frame's coordinates, so intersect them there, then move the result
			// onto the page and test that against the window. Testing the vertical
			// frame edges alone left the panel live over an element scrolled out of
			// the frame sideways, and over an element whose whole frame the artifact
			// pane had scrolled off the screen.
			const visibleLeft = iframeRect.x + Math.max(elementRect.left, 0);
			const visibleRight =
				iframeRect.x + Math.min(elementRect.right, iframeRect.width);
			const visibleTop = iframeRect.y + Math.max(elementRect.top, 0);
			const visibleBottom =
				iframeRect.y + Math.min(elementRect.bottom, iframeRect.height);
			const isAnchorVisible =
				!isMeasurable ||
				(meetsFrameAxis(
					elementRect.left,
					elementRect.right,
					iframeRect.width,
				) &&
					meetsFrameAxis(
						elementRect.top,
						elementRect.bottom,
						iframeRect.height,
					) &&
					visibleRight > 0 &&
					visibleLeft < window.innerWidth &&
					visibleBottom > 0 &&
					visibleTop < window.innerHeight);

			// The email background and the main content column are as tall as the
			// whole email, so their boxes end thousands of pixels below the visible
			// frame. A box that tall tells us nothing about where to put the panel,
			// so anchor to the spot the customer clicked instead.
			if (
				offset &&
				(elementRect.height > iframeRect.height ||
					elementRect.width > iframeRect.width)
			) {
				// Such an element still fills the frame after the clicked spot has
				// scrolled away, so keep the panel beside the part that is on screen
				// rather than letting it ride out over the app around the frame.
				// Both axes clamp: a wide element scrolls sideways as well.
				const pointX = Math.min(
					Math.max(elementRect.left + offset.x, 0),
					iframeRect.width,
				);
				const pointY = Math.min(
					Math.max(elementRect.top + offset.y, 0),
					iframeRect.height,
				);
				return {
					anchorRect: new DOMRect(
						iframeRect.x + pointX,
						iframeRect.y + pointY,
						0,
						0,
					),
					isAnchorVisible,
					frameRect: iframeRect,
				};
			}

			return {
				anchorRect: new DOMRect(
					elementRect.x + iframeRect.x,
					elementRect.y + iframeRect.y,
					elementRect.width,
					elementRect.height,
				),
				isAnchorVisible,
				frameRect: iframeRect,
			};
		},
		[iframeRef],
	);

	const findBwAncestor = useCallback(
		(target: EventTarget | null): HTMLElement | null => {
			const element = elementFromTarget(target);
			if (!element) return null;
			const bwEl = element.closest('[id^="bw-"]') as HTMLElement | null;
			if (!bwEl) return null;
			return descriptorMap.current.has(bwEl.id) ? bwEl : null;
		},
		[],
	);

	const clearHover = useCallback(() => {
		const el = hoveredElementRef.current;
		if (el && el !== selectedElementRef.current) {
			if (el.dataset.bwEditorOutline) {
				el.style.outline = el.dataset.bwPrevOutline || '';
				delete el.dataset.bwEditorOutline;
				delete el.dataset.bwPrevOutline;
			}
		}
		hoveredElementRef.current = null;
	}, []);

	const handlePointerMove = useCallback(
		(e: PointerEvent) => {
			const el = findBwAncestor(e.target);
			if (el === hoveredElementRef.current) return;

			clearHover();

			if (el && el !== selectedElementRef.current) {
				if (!el.dataset.bwEditorOutline) {
					el.dataset.bwPrevOutline = el.style.outline || '';
				}
				el.style.outline = HOVER_OUTLINE;
				el.dataset.bwEditorOutline = '1';
				hoveredElementRef.current = el;
			}
		},
		[findBwAncestor, clearHover],
	);

	const handlePointerLeave = useCallback(() => {
		clearHover();
	}, [clearHover]);

	const clearSelection = useCallback(() => {
		// A panel field sends its value on blur and at no other time. The colour
		// field is one: react-aria binds its commit to onBlur. React dispatches no
		// blur when it unmounts a focused field, so a typed hex would be lost. So
		// blur first, which commits, then close. The email frame keeps its focus,
		// because the caret for an in-place text edit lives inside it.
		// Only blur focus that actually sits inside the popup being torn down —
		// a popup with no focus trap can lose focus to an unrelated app control
		// (e.g. Shift+Tab out of its first field), and blurring that control here
		// would be an unrelated side effect of closing this panel.
		const active = document.activeElement;
		if (
			active instanceof HTMLElement &&
			active !== iframeRef.current &&
			active.closest('[data-bw-editor-popup]')
		) {
			active.blur();
		}

		clearHover();
		if (selectedElementRef.current) {
			if (selectedElementRef.current.dataset.bwEditorOutline) {
				selectedElementRef.current.style.outline =
					selectedElementRef.current.dataset.bwPrevOutline || '';
				delete selectedElementRef.current.dataset.bwEditorOutline;
				delete selectedElementRef.current.dataset.bwPrevOutline;
			}
		}
		selectedElementRef.current = null;
		clickOffsetRef.current = null;
		setSelection(null);
	}, [clearHover, iframeRef]);

	const handleClick = useCallback(
		(e: MouseEvent) => {
			// A link inside the email would navigate the frame to its destination
			// site. The preview then becomes a cross-origin document, contentDocument
			// reads null, and every later edit silently does nothing while the panel
			// stays open over a dead frame. The customer edits the email here, so no
			// link inside it should ever navigate.
			if (elementFromTarget(e.target)?.closest('a[href]')) {
				e.preventDefault();
			}

			const el = findBwAncestor(e.target);

			const selected = selectedElementRef.current;
			const role = el ? descriptorMap.current.get(el.id)?.role : undefined;

			if (!el) {
				clearSelection();
				return;
			}

			if (selected) {
				if (el === selected) {
					// Text and headings are editable in place, so a second click on the
					// selected element is the customer moving the caret. Leave the panel
					// where it is, otherwise it jumps around while they type.
					if (role === 'text' || role === 'heading') return;
					clearSelection();
					return;
				}

				// The customer clicked something that wraps what is already selected,
				// such as the email background behind an open panel. Treat that as
				// "put the panel away" rather than as a request to edit the wrapper.
				if (el.contains(selected)) {
					clearSelection();
					return;
				}
			}

			// Clear previous selection
			if (selectedElementRef.current) {
				if (selectedElementRef.current.dataset.bwEditorOutline) {
					selectedElementRef.current.style.outline =
						selectedElementRef.current.dataset.bwPrevOutline || '';
					delete selectedElementRef.current.dataset.bwEditorOutline;
					delete selectedElementRef.current.dataset.bwPrevOutline;
				}
			}

			const descriptor = descriptorMap.current.get(el.id);
			if (!descriptor) return;

			if (!el.dataset.bwEditorOutline) {
				el.dataset.bwPrevOutline = el.style.outline || '';
			}
			el.style.outline = SELECTED_OUTLINE;
			el.dataset.bwEditorOutline = '1';
			selectedElementRef.current = el;

			const elementRect = el.getBoundingClientRect();
			clickOffsetRef.current = {
				x: e.clientX - elementRect.left,
				y: e.clientY - elementRect.top,
			};

			const anchor = computeAnchor(el, clickOffsetRef.current);
			if (!anchor) return;

			setSelection({ descriptor, ...anchor });
		},
		[findBwAncestor, computeAnchor, clearSelection],
	);

	const debouncedSerialize = useCallback(() => {
		clearTimeout(serializeTimerRef.current);
		serializeTimerRef.current = setTimeout(onSerialize, DEBOUNCE_MS);
	}, [onSerialize]);

	const applyEdit = useCallback(
		(descriptor: EditableDescriptor, patch: Partial<EditPatch>) => {
			const iframeDoc = getIframeDoc();
			if (!iframeDoc) return;

			const element = iframeDoc.getElementById(descriptor.id);
			if (!element) return;

			for (const [key, value] of Object.entries(patch)) {
				if (value !== undefined) {
					writePropertyValue(
						element,
						descriptor.role,
						key as keyof EditPatch,
						value,
					);
				}
			}

			debouncedSerialize();
		},
		[getIframeDoc, debouncedSerialize],
	);

	// Recalculate anchor rect on scroll/resize
	const updateAnchorRect = useCallback(() => {
		const el = selectedElementRef.current;
		if (!el) return;
		const anchor = computeAnchor(el, clickOffsetRef.current);
		if (!anchor) return;
		setSelection((prev) => {
			if (!prev) return null;
			// Return the same object when nothing moved. The frame watch below runs
			// on every animation frame, so a new object here would re-render the
			// panel about sixty times a second on a still page.
			if (
				prev.isAnchorVisible === anchor.isAnchorVisible &&
				sameRect(prev.anchorRect, anchor.anchorRect) &&
				sameRect(prev.frameRect, anchor.frameRect)
			) {
				return prev;
			}
			return { ...prev, ...anchor };
		});
	}, [computeAnchor]);

	const handleEscape = useCallback(
		(e: KeyboardEvent) => {
			if (e.key !== 'Escape' || !selectedElementRef.current) return;

			// The strategy dock menu closes on an Escape it reads at window, and
			// window follows document on the bubble path. Without this, one press
			// closes the panel and the dock menu together. The guard above keeps
			// the press available to them while no panel is open.
			e.stopPropagation();

			clearSelection();
		},
		[clearSelection],
	);

	// A layout change around the editor moves the frame without firing a scroll
	// event or a resize event. The save-error banner mounts above the editor and
	// pushes the email down about 50 pixels, and the panel then covers the very
	// element it edits. The platform reports no such move, so re-measure each
	// frame while a panel is open. updateAnchorRect returns the previous state
	// when nothing moved, so a still page costs no render.
	const hasSelection = selection !== null;
	useEffect(() => {
		if (!hasSelection) return;

		let handle = 0;
		const tick = () => {
			updateAnchorRect();
			handle = requestAnimationFrame(tick);
		};
		// A backgrounded tab has no layout to track, so pause the poll on
		// visibilitychange instead of burning a frame budget it can't spend.
		const start = () => {
			if (handle || document.hidden) return;
			handle = requestAnimationFrame(tick);
		};
		const stop = () => {
			cancelAnimationFrame(handle);
			handle = 0;
		};
		const handleVisibilityChange = () => {
			if (document.hidden) stop();
			else start();
		};

		start();
		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			stop();
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [hasSelection, updateAnchorRect]);

	// Attach event listeners
	useEffect(() => {
		const iframeDoc = getIframeDoc();
		if (!iframeDoc || descriptors.length === 0) return;

		iframeDoc.addEventListener('pointermove', handlePointerMove);
		iframeDoc.addEventListener('pointerleave', handlePointerLeave);
		iframeDoc.addEventListener('click', handleClick);
		// A key pressed inside the email never reaches the parent document, and a
		// text element keeps focus inside the frame while it is edited. Listen on
		// both documents so Escape works wherever the focus sits.
		iframeDoc.addEventListener('keydown', handleEscape);
		document.addEventListener('keydown', handleEscape);
		iframeDoc.addEventListener('scroll', updateAnchorRect, true);
		window.addEventListener('scroll', updateAnchorRect, true);
		window.addEventListener('resize', updateAnchorRect);

		return () => {
			iframeDoc.removeEventListener('pointermove', handlePointerMove);
			iframeDoc.removeEventListener('pointerleave', handlePointerLeave);
			iframeDoc.removeEventListener('click', handleClick);
			iframeDoc.removeEventListener('keydown', handleEscape);
			document.removeEventListener('keydown', handleEscape);
			iframeDoc.removeEventListener('scroll', updateAnchorRect, true);
			window.removeEventListener('scroll', updateAnchorRect, true);
			window.removeEventListener('resize', updateAnchorRect);
			clearTimeout(serializeTimerRef.current);
		};
	}, [
		getIframeDoc,
		descriptors,
		handlePointerMove,
		handlePointerLeave,
		handleClick,
		handleEscape,
		updateAnchorRect,
	]);

	return { selection, applyEdit, clearSelection, debouncedSerialize };
}
